import React, { useEffect, useState, useCallback, useRef } from 'react';
import EntityTable from '../components/EntityTable';
import EntityDetailPanel from '../components/EntityDetailPanel';
import Toolbar, { Pager } from '../components/Toolbar';
import RelLink from '../components/RelLink';
import { ImageWithHoverPreview } from '../../components/CardHoverPreview/CardHoverPreview';
import { Certs, Cards, Promos, Pricing } from '../api/admin';
import { formatEastern } from '../../utils/formatDate';
import { priceChartingUrl } from '../../utils/priceCharting';

const PAGE_SIZE = 50;

// Grade number pulled off the cert's grade string (e.g. "PSA 10" -> 10), used to
// bold the matching PriceCharting price. Mirrors the broker portal's CertCells.
const gradeNumber = (cert) => {
    if (!cert?.grade) return null;
    const m = String(cert.grade).match(/\d+/);
    return m ? parseInt(m[0], 10) : null;
};

const money = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);
    return `$${n.toFixed(2)}`;
};

// Compact date (MM/DD/YY) for the "last priced" line under the grade breakdown.
const formatPriceDate = (dateString) => formatEastern(dateString, {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
});

// PriceCharting per-grade breakdown (10/9/8/7), bolding the cert's own grade.
// Same shape/behaviour as the broker portal so the two portals read alike.
const PriceChartingGrades = ({ cert }) => {
    if (!cert?.pricing_data) return <span className="muted">—</span>;
    const myGrade = gradeNumber(cert);
    const updated = formatPriceDate(cert.pricing_data.date_updated);
    return (
        <div className="price-charting-prices">
            {[10, 9, 8, 7].map((g) => {
                const price = cert.pricing_data[`psa_${g}`];
                return (
                    <div key={g} className={myGrade === g ? 'bold-price' : ''}>
                        <span className="grade-label">{g}:</span>{' '}
                        {price ? `$${price}` : 'N/A'}
                    </div>
                );
            })}
            {updated && <div className="price-updated">Updated {updated}</div>}
        </div>
    );
};

const CertsTab = ({ filters, goTo }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(Number(filters.page) || 1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchCert, setSearchCert] = useState(filters.certNumber || '');
    const [searchName, setSearchName] = useState('');
    const [debouncedName, setDebouncedName] = useState('');
    const firstNameRun = useRef(true);
    const [selected, setSelected] = useState(null);
    const [mapTarget, setMapTarget] = useState(null);
    const [promos, setPromos] = useState([]);
    const [pricingBusy, setPricingBusy] = useState(false);
    const [pricingMsg, setPricingMsg] = useState(null);

    useEffect(() => {
        setSearchCert(filters.certNumber || '');
        setPage(Number(filters.page) || 1);
    }, [filters.certNumber, filters.page]);

    // Debounce the card-name input so we re-query shortly after typing stops
    // (partial match — typing "a" searches for anything containing "a").
    useEffect(() => {
        const t = setTimeout(() => setDebouncedName(searchName.trim()), 300);
        return () => clearTimeout(t);
    }, [searchName]);

    // A new name query starts back at page 1 (but not on first mount).
    useEffect(() => {
        if (firstNameRun.current) { firstNameRun.current = false; return; }
        setPage(1);
    }, [debouncedName]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await Certs.list({
                page,
                count: PAGE_SIZE,
                certNumber: searchCert || undefined,
                cardName: debouncedName || undefined,
            });
            setRows(result.certs || []);
            const total = result.total ?? result.total_amount ?? 0;
            setTotalPages(total ? Math.ceil(total / PAGE_SIZE) : (result.certs?.length === PAGE_SIZE ? page + 1 : page));
        } catch (e) {
            setError(e.message);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [page, searchCert, debouncedName]);

    useEffect(() => { load(); }, [load]);

    const applySearch = () =>
        goTo({
            tab: 'certs',
            certNumber: searchCert || undefined,
            page: 1,
        });

    const clearFilters = () => {
        setSearchName('');
        goTo({ tab: 'certs' });
    };

    const refreshAllPricing = async () => {
        if (pricingBusy) {
            return;
        }
        if (!window.confirm('Update PriceCharting pricing for every active cert? This can take a while.')) {
            return;
        }
        setPricingBusy(true);
        setPricingMsg(null);
        try {
            const result = await Pricing.refreshAll();
            const count = Array.isArray(result) ? result.length : 0;
            setPricingMsg(`Updated pricing for ${count} cert${count === 1 ? '' : 's'}.`);
            load();
        } catch (e) {
            setPricingMsg(`Failed: ${e.message}`);
        } finally {
            setPricingBusy(false);
        }
    };

    const openMap = async (cert) => {
        setMapTarget(cert);
        try {
            const result = await Promos.list();
            setPromos(result.promos || []);
        } catch (e) {
            // non-fatal
        }
    };

    // Column order mirrors the broker portal's cert table (image → cert # → card
    // → grade → set …) so the two portals read the same way.
    const columns = [
        {
            key: 'images',
            header: 'Img',
            width: '76px',
            render: (r) =>
                r.cert_images?.front_image_url ? (
                    <ImageWithHoverPreview
                        src={r.cert_images.front_image_url}
                        alt={r.name ? `Front: ${r.name}` : 'Cert front'}
                        className="cert-image"
                    />
                ) : (
                    <span className="cert-image-placeholder" aria-hidden="true">—</span>
                ),
        },
        {
            key: 'cert_number',
            header: 'Cert #',
            render: (r) => (
                <a
                    href={`https://www.psacard.com/cert/${r.cert_number}/psa`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="external-link"
                    onClick={(e) => e.stopPropagation()}
                    title="View on PSA"
                >
                    {r.cert_number}
                </a>
            ),
        },
        {
            key: 'name',
            header: 'Card',
            render: (r) =>
                r.card?.id ? (
                    <RelLink tab="cards" filters={{ cardId: r.card.id }} goTo={goTo}>
                        {r.name || r.card.name}
                    </RelLink>
                ) : (r.name || '—'),
        },
        { key: 'grade', header: 'Grade' },
        {
            key: 'set',
            header: 'Set',
            render: (r) =>
                r.card?.set?.name ? (
                    <RelLink tab="sets" filters={{ setName: r.card.set.name }} goTo={goTo}>
                        {r.card.set.name}
                    </RelLink>
                ) : (r.set || '—'),
        },
        { key: 'set_number', header: '#', width: '60px' },
        { key: 'year', header: 'Year', width: '70px' },
        {
            key: 'pricecharting',
            header: 'PriceCharting',
            render: (r) => <PriceChartingGrades cert={r} />,
        },
        {
            key: 'gamestop',
            header: 'GS',
            width: '70px',
            render: (r) => {
                const v = money(r.game_stop_pricing?.offer_price_pro_cash);
                return v || <span className="muted">—</span>;
            },
        },
        {
            key: 'promo',
            header: 'Promo',
            render: (r) =>
                r.promo?.promo_id ? (
                    <RelLink tab="promos" filters={{ promoId: r.promo.promo_id }} goTo={goTo}>
                        {r.promo.display_name || r.promo.promo_id}
                    </RelLink>
                ) : '—',
        },
    ];

    const sectionsFor = (r) => [
        {
            title: 'Cert',
            rows: [
                {
                    label: 'Cert #',
                    render: () => (
                        <a
                            href={`https://www.psacard.com/cert/${r.cert_number}/psa`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="external-link"
                        >
                            {r.cert_number}
                        </a>
                    ),
                },
                {
                    label: 'Card',
                    render: () =>
                        r.card?.id ? (
                            <RelLink tab="cards" filters={{ cardId: r.card.id }} goTo={goTo}>
                                {r.name || r.card.name}
                            </RelLink>
                        ) : (r.name || '—'),
                },
                { label: 'Grade', value: r.grade },
                { label: 'Brand', value: r.brand },
                {
                    label: 'Set',
                    render: () =>
                        r.card?.set?.name ? (
                            <RelLink tab="sets" filters={{ setName: r.card.set.name }} goTo={goTo}>
                                {r.card.set.name}
                            </RelLink>
                        ) : (r.set || '—'),
                },
                { label: 'Set #', value: r.set_number },
                { label: 'Year', value: r.year },
                { label: 'Active', value: r.is_active ? 'Yes' : 'No' },
            ],
        },
        {
            title: 'External Mappings',
            rows: [
                {
                    label: 'Promo',
                    render: () =>
                        r.promo?.promo_id ? (
                            <RelLink tab="promos" filters={{ promoId: r.promo.promo_id }} goTo={goTo}>
                                {r.promo.display_name || r.promo.promo_id}
                            </RelLink>
                        ) : '—',
                },
                { label: 'PriceCharting ID', value: r.price_charting_id },
                { label: 'GameStop ID', value: r.game_stop_id },
                { label: 'Spec ID', value: r.spec_id },
            ],
        },
        {
            title: 'Pricing',
            rows: [
                {
                    label: 'Product',
                    render: () =>
                        r.pricing_data?.product_name && r.price_charting_id ? (
                            <a
                                href={priceChartingUrl(r)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="external-link"
                            >
                                {r.pricing_data.product_name}
                            </a>
                        ) : (r.pricing_data?.product_name || '—'),
                },
                {
                    label: 'PriceCharting',
                    render: () => <PriceChartingGrades cert={r} />,
                },
                { label: 'PriceCharting price', value: money(r.price_charting_price) || '—' },
                { label: 'GameStop', value: money(r.game_stop_pricing?.offer_price_pro_cash) || '—' },
            ],
        },
        {
            title: 'Related',
            rows: [
                {
                    label: 'Listings',
                    render: () => (
                        <RelLink tab="listings" filters={{ certNumber: r.cert_number }} goTo={goTo}>
                            View listings →
                        </RelLink>
                    ),
                },
                {
                    label: 'Orders',
                    render: () => (
                        <RelLink tab="orders" filters={{ certNumber: r.cert_number }} goTo={goTo}>
                            View orders →
                        </RelLink>
                    ),
                },
            ],
        },
        {
            title: 'Mapping Check',
            rows: [{
                label: ' ',
                render: () => (
                    <div className="detail-images mapping-compare">
                        <figure>
                            {r.cert_images?.front_image_url ? (
                                <a href={`https://gradies.com/listing/${r.cert_number}`} target="_blank" rel="noreferrer">
                                    <img src={r.cert_images.front_image_url} alt="PSA cert" />
                                </a>
                            ) : <div className="muted">No cert image</div>}
                            <figcaption>Cert #{r.cert_number}<br />{r.name}<br />{r.grade}</figcaption>
                        </figure>
                        <figure>
                            {cardImageUrl(r.card) ? (
                                <img src={cardImageUrl(r.card)} alt={r.card.name} />
                            ) : <div className="muted">No card mapped</div>}
                            <figcaption>Card<br />{r.card ? `${r.card.name} #${r.card.number}` : '—'}</figcaption>
                        </figure>
                        <figure>
                            {r.pricing_data?.tcgplayer_product?.image_url ? (
                                <a href={priceChartingUrl(r)} target="_blank" rel="noreferrer">
                                    <img src={r.pricing_data.tcgplayer_product.image_url} alt="PriceCharting product" />
                                </a>
                            ) : <div className="muted">{r.price_charting_id ? 'No TCGplayer image yet' : 'No PriceCharting mapping'}</div>}
                            <figcaption>
                                PriceCharting<br />{r.pricing_data?.product_name || '—'}
                                {r.pricing_data?.tcgplayer_product && (
                                    <>
                                        <br />
                                        <a href={r.pricing_data.tcgplayer_product.url} target="_blank" rel="noreferrer">
                                            TCGplayer: {r.pricing_data.tcgplayer_product.set_name} #{r.pricing_data.tcgplayer_product.number}
                                        </a>
                                    </>
                                )}
                            </figcaption>
                        </figure>
                    </div>
                ),
            }],
        },
    ];

    return (
        <div className="admin-tab-panel">
            <Toolbar>
                <input
                    placeholder="Cert #"
                    value={searchCert}
                    onChange={(e) => setSearchCert(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && applySearch()}
                />
                <button className="btn-primary" onClick={applySearch}>Search</button>
                <input
                    placeholder="Card name"
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                />
                <button className="btn-ghost" onClick={clearFilters}>Clear</button>
                <button
                    className="btn-primary"
                    onClick={refreshAllPricing}
                    disabled={pricingBusy}
                    style={{ marginLeft: 'auto' }}
                    title="Refresh PriceCharting pricing for every active cert"
                >
                    {pricingBusy ? 'Updating pricing…' : 'Update all pricing'}
                </button>
            </Toolbar>

            {pricingMsg && (
                <p className={pricingMsg.startsWith('Failed') ? 'error-message' : 'loading'}>
                    {pricingMsg}
                </p>
            )}

            {loading && <p className="loading">Loading…</p>}
            {error && <p className="error-message">Error: {error}</p>}

            {!loading && !error && (
                <EntityTable
                    columns={columns}
                    rows={rows}
                    onRowClick={(r) => setSelected(r)}
                    actions={(r) => (
                        <button className="btn-primary btn-sm" onClick={() => openMap(r)}>
                            Map
                        </button>
                    )}
                    emptyMessage="No certs match these filters."
                />
            )}

            <Pager
                page={page}
                totalPages={totalPages}
                onChange={(p) => goTo({
                    tab: 'certs',
                    certNumber: searchCert || undefined,
                    page: p,
                })}
            />

            <EntityDetailPanel
                open={!!selected}
                title={selected ? `Cert ${selected.cert_number}` : ''}
                onClose={() => setSelected(null)}
                sections={selected ? sectionsFor(selected) : []}
                footer={selected ? (
                    <button className="btn-ghost" onClick={() => openMap(selected)}>
                        Open Mapping
                    </button>
                ) : null}
            />

            {mapTarget && (
                <MappingModal
                    cert={mapTarget}
                    promos={promos}
                    onClose={() => setMapTarget(null)}
                    onSaved={() => { setMapTarget(null); load(); }}
                />
            )}
        </div>
    );
};

// Card art: our stored images, else the Pokémon TCG CDN path built from the card's
// TCG id ("sm1-127" -> images.pokemontcg.io/sm1/127.png).
const cardImageUrl = (card) => {
    if (!card) return null;
    if (card.images?.large || card.images?.small) return card.images.large || card.images.small;
    const dash = card.id ? card.id.indexOf('-') : -1;
    return dash > 0 ? `https://images.pokemontcg.io/${card.id.slice(0, dash)}/${card.id.slice(dash + 1)}.png` : null;
};

const MappingImage = ({ src, alt, href, empty }) => {
    if (!src) return <div className="mapping-image-empty">{empty}</div>;
    const img = <img src={src} alt={alt} />;
    return href ? <a href={href} target="_blank" rel="noreferrer">{img}</a> : img;
};

// Cert | Card | PriceCharting side by side, each with the tool that changes it.
// Card and PriceCharting saves PATCH the cert, and the API applies them to every
// cert with the same PSA spec (new certs of that spec copy them on create).
const MappingModal = ({ cert: initialCert, promos, onClose, onSaved }) => {
    const [cert, setCert] = useState(initialCert);
    const [specCertCount, setSpecCertCount] = useState(null);
    const [cardName, setCardName] = useState('');
    const [cardNumber, setCardNumber] = useState(String(initialCert.set_number || '').replace(/^0+/, ''));
    const [cardResults, setCardResults] = useState([]);
    const [cardSearching, setCardSearching] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const [priceChartingId, setPriceChartingId] = useState(String(initialCert.price_charting_id ?? ''));
    const [promoId, setPromoId] = useState(initialCert.promo?.promo_id || '');
    const [gameStopId, setGameStopId] = useState(String(initialCert.game_stop_id ?? ''));
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [busy, setBusy] = useState(null);
    const [changed, setChanged] = useState(false);

    useEffect(() => {
        if (!initialCert.spec_id) return;
        Certs.list({ specId: initialCert.spec_id, hasImages: false, count: 1000 })
            .then((result) => setSpecCertCount((result.certs || []).length))
            .catch(() => setSpecCertCount(null));
    }, [initialCert.spec_id]);

    const searchCards = useCallback(async () => {
        if (!cardName.trim() && !cardNumber.trim()) return;
        setCardSearching(true);
        setError(null);
        try {
            const result = await Cards.list({ count: 60, name: cardName.trim() || undefined, number: cardNumber.trim() || undefined });
            setCardResults(result.cards || []);
        } catch (e) {
            setError(e.message);
        } finally {
            setCardSearching(false);
        }
    }, [cardName, cardNumber]);

    // Open with the cert's set number already searched, so every #N card is on screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { searchCards(); }, []);

    const run = async (section, request, successMessage) => {
        setBusy(section);
        setError(null);
        setMessage(null);
        try {
            const updated = await request();
            if (updated?.cert_number) setCert(updated);
            setChanged(true);
            setMessage(successMessage);
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(null);
        }
    };

    const specNote = cert.spec_id
        ? `Applies to every cert with spec ${cert.spec_id}${specCertCount ? ` (${specCertCount} cert${specCertCount === 1 ? '' : 's'})` : ''} and future ones.`
        : 'This cert has no PSA spec, so only this cert changes.';

    const saveCard = () => run('card',
        () => Certs.patch(cert.cert_number, { card_id: selectedCard.db_id }),
        `Card set to ${selectedCard.name} #${selectedCard.number}.`);

    const savePriceCharting = () => run('pricecharting',
        () => Certs.patch(cert.cert_number, { price_charting_id: priceChartingId.trim() }),
        priceChartingId.trim() ? `PriceCharting set to ${priceChartingId.trim()} and repriced.` : 'PriceCharting mapping cleared.');

    const savePromo = () => run('promo', async () => {
        const promo = promos.find((p) => p.promo_id === promoId);
        if (!promo) throw new Error('Pick a promo first');
        await Promos.map(cert.cert_number, promo.id);
        return (await Certs.list({ certNumber: cert.cert_number, hasImages: false })).certs?.[0];
    }, 'Promo saved.');

    const saveGameStop = () => run('gamestop',
        () => Certs.patch(cert.cert_number, { game_stop_id: Number(gameStopId) }),
        'GameStop ID saved.');

    const close = () => (changed ? onSaved() : onClose());
    const card = cert.card;
    const pricing = cert.pricing_data;
    const tcgPlayerProduct = pricing?.tcgplayer_product;
    const priceChartingSearchUrl = `https://www.pricecharting.com/search-products?type=prices&q=${encodeURIComponent(`${card?.name || cert.name || ''} ${card?.number || cert.set_number || ''}`.trim())}`;

    return (
        <div className="modal-overlay" onClick={close}>
            <div className="modal-content mapping-modal" onClick={(e) => e.stopPropagation()}>
                <h3>Map Cert #{cert.cert_number}</h3>
                <p className="mapping-spec-note">{specNote}</p>
                {error && <p className="error-message">{error}</p>}
                {message && <p className="mapping-success">{message}</p>}

                <div className="mapping-columns">
                    <section className="mapping-column">
                        <h4>Cert</h4>
                        <MappingImage
                            src={cert.cert_images?.front_image_url}
                            alt={`PSA cert ${cert.cert_number}`}
                            href={`https://www.psacard.com/cert/${cert.cert_number}/psa`}
                            empty="No cert image"
                        />
                        <dl className="mapping-facts">
                            <dt>PSA Name</dt><dd>{cert.name || '—'}</dd>
                            <dt>Grade</dt><dd>{cert.grade || '—'}</dd>
                            <dt>Set</dt><dd>{cert.set || '—'}</dd>
                            <dt>Number</dt><dd>{cert.set_number || '—'}</dd>
                            <dt>Year</dt><dd>{cert.year || '—'}</dd>
                            <dt>Spec</dt><dd>{cert.spec_id || '—'}</dd>
                        </dl>
                    </section>

                    <section className="mapping-column">
                        <h4>Card</h4>
                        <MappingImage src={cardImageUrl(card)} alt={card?.name || 'Card'} empty="No card mapped" />
                        <dl className="mapping-facts">
                            <dt>Name</dt><dd>{card?.name || '—'}</dd>
                            <dt>Set</dt><dd>{card?.set?.name || '—'}</dd>
                            <dt>Number</dt><dd>{card?.number || '—'}</dd>
                            <dt>Card ID</dt><dd>{cert.card_id || '—'}</dd>
                        </dl>
                        <div className="mapping-tool">
                            <div className="mapping-search-row">
                                <input
                                    placeholder="Card name"
                                    value={cardName}
                                    onChange={(e) => setCardName(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && searchCards()}
                                />
                                <input
                                    className="mapping-number-input"
                                    placeholder="#"
                                    value={cardNumber}
                                    onChange={(e) => setCardNumber(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && searchCards()}
                                />
                                <button className="btn-ghost btn-sm" onClick={searchCards} disabled={cardSearching}>
                                    {cardSearching ? 'Searching…' : 'Search'}
                                </button>
                            </div>
                            <div className="mapping-card-results">
                                {cardResults.length === 0 && !cardSearching && <span className="muted">No cards found.</span>}
                                {cardResults.map((result) => (
                                    <button
                                        key={result.db_id ?? result.id}
                                        type="button"
                                        className={`mapping-card-result${selectedCard?.db_id === result.db_id ? ' selected' : ''}${cert.card_id === result.db_id ? ' current' : ''}`}
                                        onClick={() => setSelectedCard(result)}
                                        title={`${result.name} #${result.number}, ${result.set?.name || ''}`}
                                    >
                                        {cardImageUrl(result) ? <img src={cardImageUrl(result)} alt={result.name} loading="lazy" /> : <span className="mapping-card-noimage">{result.name}</span>}
                                        <span>{result.name}</span>
                                        <span className="muted">#{result.number}, {result.set?.name || '—'}</span>
                                    </button>
                                ))}
                            </div>
                            <button
                                className="section-save-button"
                                onClick={saveCard}
                                disabled={!selectedCard || busy !== null || selectedCard.db_id === cert.card_id}
                            >
                                {busy === 'card' ? 'Saving…' : selectedCard ? `Save Card: ${selectedCard.name} #${selectedCard.number}` : 'Pick a card above'}
                            </button>
                        </div>
                    </section>

                    <section className="mapping-column">
                        <h4>PriceCharting</h4>
                        <MappingImage
                            src={tcgPlayerProduct?.image_url}
                            alt={pricing?.product_name || 'PriceCharting product'}
                            href={priceChartingUrl(cert)}
                            empty={cert.price_charting_id ? (pricing ? 'No TCGplayer image for this product' : 'Not priced yet') : 'No PriceCharting mapping'}
                        />
                        <dl className="mapping-facts">
                            <dt>Product</dt><dd>{pricing?.product_name || '—'}</dd>
                            <dt>Set</dt><dd>{pricing?.set || '—'}</dd>
                            <dt>ID</dt>
                            <dd>
                                {cert.price_charting_id ? (
                                    <a href={priceChartingUrl(cert)} target="_blank" rel="noreferrer">
                                        {cert.price_charting_id}
                                    </a>
                                ) : '—'}
                            </dd>
                            <dt>TCGplayer</dt>
                            <dd>
                                {tcgPlayerProduct ? (
                                    <a href={tcgPlayerProduct.url} target="_blank" rel="noreferrer">
                                        {tcgPlayerProduct.set_name} #{tcgPlayerProduct.number}
                                    </a>
                                ) : '—'}
                            </dd>
                        </dl>
                        <PriceChartingGrades cert={cert} />
                        <div className="mapping-tool">
                            <div className="mapping-search-row">
                                <input
                                    placeholder="PriceCharting ID"
                                    value={priceChartingId}
                                    onChange={(e) => setPriceChartingId(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && busy === null && savePriceCharting()}
                                />
                            </div>
                            <a className="mapping-help-link" href={priceChartingSearchUrl} target="_blank" rel="noreferrer">
                                Search PriceCharting for this card →
                            </a>
                            <button
                                className="section-save-button"
                                onClick={savePriceCharting}
                                disabled={busy !== null || priceChartingId.trim() === String(cert.price_charting_id ?? '')}
                            >
                                {busy === 'pricecharting' ? 'Saving and pricing…' : 'Save PriceCharting'}
                            </button>
                        </div>
                    </section>
                </div>

                <div className="mapping-sections-container">
                    <div className="mapping-section">
                        <h4>Promo</h4>
                        <div className="mapping-field">
                            <select value={promoId} onChange={(e) => setPromoId(e.target.value)}>
                                <option value="">Select a promo…</option>
                                {promos.map((p) => (
                                    <option key={p.promo_id} value={p.promo_id}>
                                        {p.display_name || p.promo_id}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <button className="section-save-button" onClick={savePromo} disabled={busy !== null}>
                            {busy === 'promo' ? 'Saving…' : 'Save Promo'}
                        </button>
                    </div>
                    <div className="mapping-section">
                        <h4>GameStop</h4>
                        <div className="mapping-field">
                            <input
                                placeholder="GameStop ID"
                                value={gameStopId}
                                onChange={(e) => setGameStopId(e.target.value.replace(/[^0-9]/g, ''))}
                            />
                        </div>
                        <button className="section-save-button" onClick={saveGameStop} disabled={busy !== null || !gameStopId}>
                            {busy === 'gamestop' ? 'Saving…' : 'Save GameStop'}
                        </button>
                    </div>
                </div>

                <div className="modal-buttons">
                    <button onClick={close} disabled={busy !== null}>Close</button>
                </div>
            </div>
        </div>
    );
};

export default CertsTab;
